import { Post, PostGroup } from "../entities/post";
import { api } from "../api";
import { ReactElement, useEffect, useState } from "react";
import { WideLoader } from "./WideLoader";
import { usePostsNotifier } from "../hooks/usePostsNotifier";
import { InView } from "react-intersection-observer";
import { HiddenPostType } from "../enums/hidden-post-type";
import { CompanyPost } from "./CompanyPost";
import { CompactCompanyPost } from "./CompactCompanyPost";
import { Cog6ToothIcon } from "@heroicons/react/20/solid";
import { useRecoilState } from "recoil";
import { isConfigOpenedAtom } from "../atoms/isConfigOpenedAtom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { PostsError } from "../features/PortfolioScanner/components/PostsError";
import { formatDate } from "../services/postsGroupingService";
import { CompactLoader } from "./CompactLoader";

interface CompanyPostsProps {
  configurable: boolean;
  companyIds: number[];
  sourceIds: number[];
  postIds: number[];
  showCompanyNames: boolean;
  children?: ReactElement;
}

class CompanyPostsState {
  isLoading = true;
  isNextPageLoading = false;
  hasNextPage = false;
  page = 1;
  posts = [] as PostGroup[];
  connected = false;
  bookmarkedPostIds = [] as number[];
  hiddenPostIds = [] as number[];
  initError = false;
}

export const CompanyPosts = (props: CompanyPostsProps) => {
  const [state, setState] = useState(new CompanyPostsState());
  const [, setIsConfigOpened] = useRecoilState(isConfigOpenedAtom);
  const { onNewPost, stopWaitingForPosts } = usePostsNotifier();
  const [expandedGroups, setExpandedGroups] = useState(new Set<string>());
  const [isInitialVisit, setIsInitialVisit] = useLocalStorage(
    "initialVisit",
    true,
  );

  const loadInitialPage = () => {
    (async () => {
      try {
        const posts = await api.getPosts(
          props.companyIds,
          props.postIds,
          props.sourceIds,
          1,
        );
        setState({
          ...state,
          posts: posts,
          isLoading: false,
          hasNextPage: posts.length > 0,
          initError: false,
        });
      } catch {
        setState({ ...state, isLoading: false, initError: true });
      }
    })();
  };

  const loadNextPage = () => {
    setState({ ...state, isNextPageLoading: true });

    (async () => {
      try {
        const posts = await api.getPosts(
          props.companyIds,
          props.postIds,
          props.sourceIds,
          state.page + 1,
        );
        setState({
          ...state,
          isNextPageLoading: false,
          posts: [...state.posts, ...posts],
          hasNextPage: posts.length > 0,
          page: state.page + 1,
        });
      } catch {
        setState({ ...state, isNextPageLoading: false });
      }
    })();
  };

  const hidePost = (post: Post) => {
    state.hiddenPostIds.push(post.id);
    post.hidden = HiddenPostType.TemporarilyHidden;

    unbookmarkPost(post);
    setState({ ...state });
  };

  const unhidePost = (post: Post) => {
    state.hiddenPostIds = state.hiddenPostIds.filter((p) => p !== post.id);
    post.hidden = HiddenPostType.Visible;
    setState({ ...state });
  };

  const bookmarkPost = (post: Post) => {
    state.bookmarkedPostIds.push(post.id);
    post.bookmarked = true;
    setState({ ...state });
  };

  const unbookmarkPost = (post: Post) => {
    state.bookmarkedPostIds = state.bookmarkedPostIds.filter(
      (p) => p !== post.id,
    );
    post.bookmarked = false;
    setState({ ...state });
  };

  useEffect(() => {
    setState({ ...state, isLoading: true });
    loadInitialPage();
  }, [props.companyIds.join(",")]);

  useEffect(() => {
    onNewPost(async (companyId: string) => {
      const id = parseInt(companyId);

      if (!props.companyIds.includes(id)) return;

      const posts = await api.getPosts(
        props.companyIds,
        props.postIds,
        props.sourceIds,
        1,
      );
      setState({
        ...state,
        isLoading: false,
        hasNextPage: posts.length > 0,
        posts: posts,
      });
    });

    return () => {
      stopWaitingForPosts();
    };
  }, []);

  const groupByToMap = <T, Q>(
    array: T[],
    predicate: (value: T, index: number, array: T[]) => Q,
  ) =>
    array.reduce((map, value, index, array) => {
      const key = predicate(value, index, array);
      map.get(key)?.push(value) ?? map.set(key, [value]);
      return map;
    }, new Map<Q, T[]>());

  if (state.initError) return <PostsError retry={() => loadInitialPage()} />;
  if (state.isLoading) return <WideLoader />;
  if (!state.isLoading && state.posts.length === 0)
    return props.children || <></>;

  return (
    <>
      {Array.from(
        groupByToMap(state.posts, (x) => formatDate(x.date)).entries(),
      ).map((group: [string, PostGroup[]], i: number) => (
        <ul key={group[0]}>
          <li className="flex flex-col justify-between gap-x-6 py-5">
            <div>
              <div className="mx-auto flex max-w-5xl px-4 sm:px-6 lg:px-8">
                <div className="content-center">{group[0]}</div>
                <div className="ml-auto">
                  {props.configurable && i === 0 && (
                    <>
                      <button
                        type="button"
                        data-tooltip-id="addToScanerTooltip"
                        onClick={() => {
                          setIsConfigOpened(true);
                          setIsInitialVisit(false);
                        }}
                        className="relative ml-4 rounded-md bg-zinc-600 px-3 py-2 text-xs font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                      >
                        {isInitialVisit && (
                          <div className="absolute right-[-7px] top-[-6px] h-4 w-4 animate-pulse-custom rounded-full bg-red-500 shadow-[0_0_0px_#ff0000,0_0_0px_#ff0000]"></div>
                        )}
                        <Cog6ToothIcon aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="mx-auto mt-5 block h-[1px] max-w-6xl bg-gray-100"></div>
            </div>
            <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
              <ul className="max-w-5xl px-0 sm:px-6 lg:px-8">
                {group[1].map((postGroup) => (
                  <div key={postGroup.group}>
                    <CompanyPost
                      key={postGroup.lead.id}
                      post={postGroup.lead}
                      showCompanyNames={props.showCompanyNames}
                      bookmark={(set: boolean) =>
                        set
                          ? bookmarkPost(postGroup.lead)
                          : unbookmarkPost(postGroup.lead)
                      }
                      hide={(set: boolean) =>
                        set
                          ? hidePost(postGroup.lead)
                          : unhidePost(postGroup.lead)
                      }
                    />
                    {postGroup.topPosts.length > 0 && (
                      <div className="pb-5">
                        {postGroup.topPosts.map((post) => (
                          <CompactCompanyPost
                            key={post.id}
                            post={post}
                            showCompanyNames={props.showCompanyNames}
                          />
                        ))}
                        {postGroup.morePosts.length > 0 &&
                          (expandedGroups.has(postGroup.group) ? (
                            postGroup.morePosts.map((post) => (
                              <CompactCompanyPost
                                key={post.id}
                                post={post}
                                showCompanyNames={props.showCompanyNames}
                              />
                            ))
                          ) : (
                            <div className="flex gap-x-6 py-0.5">
                              <div className="min-w-12 text-right"></div>
                              <div className="flex-grow">
                                <a
                                  className="cursor-pointer text-xs"
                                  onClick={() => {
                                    expandedGroups.add(postGroup.group);
                                    setExpandedGroups(new Set(expandedGroups));
                                  }}
                                >
                                  Pokaż więcej ({postGroup.morePosts.length})
                                </a>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </ul>
            </div>
          </li>
        </ul>
      ))}

      {state.isNextPageLoading && <CompactLoader />}
      {state.hasNextPage && (
        <InView
          as="div"
          onChange={(inView) => {
            inView && loadNextPage();
          }}
        ></InView>
      )}
    </>
  );
};
